import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Send, AlertCircle, Eye, Calendar, Clock } from 'lucide-react';
import { PostContentCard } from '../components/posts/PostContentCard';
import { PostAIReviewCard } from '../components/posts/PostAIReviewCard';
import { PreviewCard } from '../components/posts/PreviewCard';
import { ScheduleCard } from '../components/posts/ScheduleCard';
import { TikTokComplianceCard } from '../components/posts/TikTokComplianceCard';
import { YouTubePostSettingsCard } from '../components/posts/YouTubePostSettingsCard';
import { LinkedInPostSettingsCard } from '../components/posts/LinkedInPostSettingsCard';
import { WaitingModal } from '../components/common/WaitingModal';
import { PublishConfirmationModal } from '../components/posts/PublishConfirmationModal';
import { SaveDraftInsteadModal } from '../components/posts/SaveDraftInsteadModal';
import { ApprovalCard } from '../components/posts/ApprovalCard';
import { getPost, submitPostReview } from '../services/postService';
import { useBrands } from '../hooks/useBrands';
import { useCreatePost } from '../hooks/useCreatePost';
import { useUpdatePost } from '../hooks/useUpdatePost';
import { useToast } from '../context/ToastContext';
import type { Post } from '../types/calendar';
import type { SocialPlatform } from '../components/icons/SocialIcon';
import { auth } from '../config/firebase';
import { Alert } from '../components/common/Alert';
import { ReviewCommentModal } from '../components/common/ReviewCommentModal';
import { AIBestTimeCard } from '../components/ai/AIBestTimeCard';
import { PlatformsCard } from '../components/posts/PlatformsCard';
import { BrandAndFormatCard } from '../components/posts/BrandAndFormatCard';
import { BrandsAndPlatformsCard } from '../components/posts/BrandsAndPlatformsCard';
import { MediaPostCard } from '../components/posts/MediaPostCard';
import { logger } from '../utils/logger';
import { normalizeMedia } from '../utils/mediaUtils';

// Define Reviewer type (can be moved to a types file later)
interface Reviewer {
  id: string;
  firstName: string;
  lastName: string;
  canPublish?: boolean; // Assuming this field exists on user data
  photoURL?: string; // Changed from string | null
}

export const CreateEditPostPage: React.FC = () => {
  const { t } = useTranslation();
  const { postId } = useParams<{ postId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const isEditMode = Boolean(postId);
  
  // Hooks for post operations
  const createPostMutation = useCreatePost();
  const updatePostMutation = useUpdatePost();
  
  // Constants
  const API_BASE_URL = 'https://api.socialness.ai';
  
  // Enhanced submission handling
  const handleApiResponse = (response: any) => {
    // Extract post ID from API response
    let postId = null;
    const possiblePaths = [
      'id',
      'post.id',
      'data.id',
      'data.post.id',
      'success.post.id',
      'postId',
      '_id',
      'documentId'
    ];

    for (const path of possiblePaths) {
      const value = path.split('.').reduce((obj, key) => obj?.[key], response);
      if (value) {
        postId = value;
        break;
      }
    }

    // Smart fallback: Extract real ID from media URL if no direct ID found
    if (!postId && response.media?.url) {
      const url = response.media.url;
      
      // Try to extract a meaningful ID from the URL patterns
      // Pattern 1: Look for timestamp-based IDs (like "1749510866219")
      const timestampMatch = url.match(/\/(\d{13})-/);
      if (timestampMatch) {
        const timestamp = timestampMatch[1];
        const userIdSuffix = response.userId ? response.userId.slice(-6) : '';
        postId = userIdSuffix ? `${timestamp}_${userIdSuffix}` : timestamp;
      }
      
      // Pattern 2: Look for hash-based IDs (like "a65b7b0b")
      if (!postId) {
        const hashMatch = url.match(/-([a-f0-9]{8})\./);
        if (hashMatch && response.userId) {
          const hash = hashMatch[1];
          const userIdSuffix = response.userId.slice(-6);
          postId = `${hash}_${userIdSuffix}`;
        }
      }
    }

    if (!postId) {
      logger.error('CreateEditPostPage', 'Failed to extract post ID from response', response);
      return null;
    }

    return postId;
  };
  
  // Get any passed content from ContentCreation
  const passedState = location.state as { 
    content?: string;
    platform?: string;
    hashtags?: string[];
    brandId?: string;
    imageUrl?: string | null;
  } | undefined;
  
  // State
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [content, setContent] = useState(passedState?.content || '');
  const [selectedBrand, setSelectedBrand] = useState(passedState?.brandId || '');
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>(
    passedState?.platform ? [passedState.platform as SocialPlatform] : []
  );
  const [format, setFormat] = useState<'regular' | 'reel'>('regular');
  const [media, setMedia] = useState<File | null>(null);
  const [mediaUrl, setMediaUrl] = useState<string | null>(passedState?.imageUrl || null);
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(passedState?.imageUrl ? 'image' : null);
  const [currentPostStatus, setCurrentPostStatus] = useState('draft');
  
  // This state will hold the UTC date string if scheduling is active, otherwise empty string or null.
  // It's set by ScheduleCard's onScheduleChange.
  const [activeScheduledAtUtc, setActiveScheduledAtUtc] = useState<string | null>(null);
  // This state will hold the original scheduledAt from the DB, for ScheduleCard initialization
  const [initialScheduledAtUtcFromDb, setInitialScheduledAtUtcFromDb] = useState<string | null>(null);

  const [showPublishConfirm, setShowPublishConfirm] = useState<{
    show: boolean;
    postId?: string;
  }>({ show: false });
  const [showSaveDraftModal, setShowSaveDraftModal] = useState<{
    show: boolean;
    missingPlatforms: SocialPlatform[];
  }>({ show: false, missingPlatforms: [] });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [initialData, setInitialData] = useState<Partial<Post> | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const publishBtnRef = useRef<HTMLButtonElement>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'rejected' | null>(null);

  // State for Approval
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [selectedReviewerId, setSelectedReviewerId] = useState<string | null>(null);
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [isLoadingReviewers, setIsLoadingReviewers] = useState(false);
  const [finalReviewerInfo, setFinalReviewerInfo] = useState<{ 
    id: string; 
    name: string; 
    photoURL?: string; 
    firstName?: string; 
    lastName?: string; 
  } | null>(null);
  const [finalReviewComments, setFinalReviewComments] = useState<string | null>(null);

  // Memoize reviewers length to prevent unnecessary re-renders
  const reviewersLength = useMemo(() => reviewers.length, [reviewers]);

  // Add tab state for right column
  const [activeTab, setActiveTab] = useState<'preview' | 'ai-time-suggestions' | 'planning'>('preview');

  // AI Review states
  const [aiResponse, setAiResponse] = useState<any | null>(null);
  const [copied, setCopied] = useState(false);

  // Function to clear all media states
  const handleClearMedia = () => {
    setMedia(null);
    setMediaUrl(null);
    setMediaType(null);
  };

  // TikTok settings type
  interface TikTokSettings {
    allowComments: boolean;
    allowDuets: boolean;
    allowStitch: boolean;
    discloseContent: boolean;
    privacy: '' | 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY';
    disclosureType: string[];
    userAgreedToTerms: boolean;
    isValid: boolean;
  }

  // TikTok settings
  const [tiktokSettings, setTiktokSettings] = useState<TikTokSettings>({
    allowComments: false,
    allowDuets: false,
    allowStitch: false,
    discloseContent: false,
    privacy: '',
    disclosureType: [],
    userAgreedToTerms: false,
    isValid: true
  });

  // YouTube settings
  const [youtubeSettings, setYoutubeSettings] = useState<{
    title: string;
    tags: string[];
    privacy: string;
  }>({
    title: '',
    tags: [],
    privacy: 'public'
  });
  
  // LinkedIn settings
  const [linkedinSettings, setLinkedinSettings] = useState<{
    organizationId: string;
    visibility: string;
  }>({
    organizationId: '',
    visibility: 'PUBLIC'
  });

  // Fetch brands
  const { data: brandsData, isLoading: isLoadingBrands } = useBrands();
  // Cast brands data to the correct type and memoize to prevent re-renders
  const brands = useMemo(() => (brandsData as any)?.brands || [], [brandsData]);
  
  // Get current user ID in a stable way
  const currentUserId = useMemo(() => auth.currentUser?.uid || null, [auth.currentUser?.uid]);

  // Fetch post data if in edit mode
  useEffect(() => {
    if (isEditMode && postId) {
      const fetchPost = async () => {
        try {
          setIsLoading(true);
          
          // Check if user is authenticated
          const user = auth.currentUser;
          if (!user) {
            logger.error('CreateEditPostPage', 'No authenticated user');
            setError('You must be logged in to edit a post');
            setIsLoading(false);
            return;
          }
          
          // Fetch the post data from the API
          const postData = await getPost(postId);
          
          // Safety check for postData
          if (!postData) {
            throw new Error('Invalid post data received from API');
          }
          
          // Store the raw data for debugging
          setInitialData(postData);
          
          // Cast to any to avoid TypeScript errors with property access
          // This is necessary because the API response structure might not match our type definition
          const post = postData as any;
          
          // Extract content with fallbacks
          // The content might be a string, object, or nested differently based on API changes
          let contentValue = '';
          
          // Content could be one of several potential locations or formats
          if (typeof post.content === 'string') {
            // Direct string content (most common)
            contentValue = post.content;
          } else if (post.content === null || post.content === undefined) {
            // No content
            contentValue = '';
          } else if (typeof post.content === 'object') {
            // Content is an object - might be { text: "actual content" } or similar
            if (post.content.text && typeof post.content.text === 'string') {
              contentValue = post.content.text;
            } else {
              // Try stringifying the object as fallback
              contentValue = JSON.stringify(post.content);
            }
          }
          
          // Setting all essential fields with fallbacks to ensure they're properly populated
          setContent(contentValue || '');
          
          // Brand ID with fallbacks
          const brandId = post.brandId || post.brand_id || '';
          setSelectedBrand(brandId);
          
          // Platforms with multiple fallback paths
          const platforms = 
            (Array.isArray(post.platforms) ? post.platforms : []) ||
            (post.platform ? [post.platform] : []);
          setSelectedPlatforms(platforms as SocialPlatform[]);
          
          // Format with fallbacks and validation
          const formatValue = post.format || (post.media?.type === 'video' ? 'reel' : 'regular');
          // Ensure format is only 'regular' or 'reel'
          const validFormat = (formatValue === 'reel') ? 'reel' : 'regular';
          setFormat(validFormat);
          
          // Media URL and type with fallbacks - updated to handle new array format
          let mediaUrlValue = null;
          let mediaTypeValue = null;
          
          // Use normalizeMedia utility for consistent media parsing
          const normalizedMedia = normalizeMedia(post.media);
          if (normalizedMedia) {
            mediaUrlValue = normalizedMedia.url;
            mediaTypeValue = normalizedMedia.type;
          } else if (post.mediaUrl || post.media_url) {
            // Alternative direct media URL property
            mediaUrlValue = post.mediaUrl || post.media_url;
            mediaTypeValue = post.mediaType || post.media_type || 
                         (mediaUrlValue?.includes('.mp4') ? 'video' : 'image');
          }
          
          // Check additional potential locations for media URL
          if (!mediaUrlValue) {
            // Check for nested media objects
            if (post.data?.media?.url) {
              mediaUrlValue = post.data.media.url;
              mediaTypeValue = post.data.media.type || 'image';
            } else if (post.post?.media?.url) {
              mediaUrlValue = post.post.media.url;
              mediaTypeValue = post.post.media.type || 'image'; 
            }
          }
          
          setMediaUrl(mediaUrlValue);
          setMediaType(mediaTypeValue as 'image' | 'video' | null);
          
          // Status with fallbacks
          setCurrentPostStatus(post.status || 'draft');
          
          // Schedule date with fallbacks
          const scheduleDateValue = post.scheduledAt || post.scheduled_at || post.scheduledFor || null;
          setActiveScheduledAtUtc(scheduleDateValue);
          setInitialScheduledAtUtcFromDb(scheduleDateValue);
          
          // Handle TikTok settings if available
          if (post.platformSettings?.tiktok || post.tiktokSettings || post.tiktok_settings) {
            // First try to get settings from platformSettings (API response)
            const platformTikTokData = post.platformSettings?.tiktok || {};
            // Fallback to legacy settings if needed
            const legacyTikTokData = post.tiktokSettings || post.tiktok_settings || {};
            // Combine both, prioritizing platform settings
            const tikTokData = { ...legacyTikTokData, ...platformTikTokData };
            
            // Map the privacy value from Firestore/API format
            let privacyValue = '';
            const rawPrivacy = tikTokData.privacy_level || tikTokData.privacy;
            
            // Map privacy values from API/Firestore format to component format
            switch(rawPrivacy?.toUpperCase?.()) {
              case 'PUBLIC':
              case 'PUBLIC_TO_EVERYONE':
                privacyValue = 'PUBLIC_TO_EVERYONE';
                break;
              case 'MUTUAL_FOLLOW_FRIENDS':
              case 'MUTUAL_FOLLOWERS':
                privacyValue = 'MUTUAL_FOLLOW_FRIENDS';
                break;
              case 'FOLLOWER_OF_CREATOR':
              case 'FOLLOWERS':
                privacyValue = 'FOLLOWER_OF_CREATOR';
                break;
              case 'PRIVATE':
              case 'SELF_ONLY':
                privacyValue = 'SELF_ONLY';
                break;
              default:
                privacyValue = '';
            }
            
            // Create settings object with detailed logging
            const newTikTokSettings = {
              allowComments: !tikTokData.disable_comment,
              allowDuets: !tikTokData.disable_duet,
              allowStitch: !tikTokData.disable_stitch,
              discloseContent: Boolean(tikTokData.brand_safety_brand_owned || tikTokData.brand_safety_paid_partnership),
              privacy: privacyValue as '' | 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY',
              disclosureType: [
                ...(tikTokData.brand_safety_brand_owned ? ['brand'] : []),
                ...(tikTokData.brand_safety_paid_partnership ? ['branded'] : [])
              ],
              userAgreedToTerms: tikTokData.userAgreedToTerms || tikTokData.user_agreed_to_terms || false,
              isValid: true
            };

            setTiktokSettings(newTikTokSettings);
          } else {
            logger.info('CreateEditPostPage', 'No TikTok settings found in post data');
          }
          
          // Handle YouTube settings if available
          // Updated to check platformSettings.youtube first
          const platformYouTubeData = post.platformSettings?.youtube;
          const legacyYouTubeData = post.youtubeSettings || post.youtube_settings;
          
          if (platformYouTubeData || legacyYouTubeData) {
            const ytData = { ...legacyYouTubeData, ...platformYouTubeData }; // Prioritize platformSettings
            
            setYoutubeSettings({
              title: ytData.title || '',
              tags: Array.isArray(ytData.tags) ? ytData.tags : [],
              privacy: ytData.privacy || 'public'
            });
          } else {
            logger.info('CreateEditPostPage', 'No YouTube settings found in post data');
          }
          
          // Handle LinkedIn settings if available
          if (post.linkedinSettings || post.linkedin_settings) {
            const liData = post.linkedinSettings || post.linkedin_settings;
            
            setLinkedinSettings({
              organizationId: liData.organizationId || liData.organization_id || '',
              visibility: liData.visibility || 'PUBLIC'
            });
          }

          // Handle Approval settings directly from post fields
          if (post.needsReview !== undefined) { // Check if needsReview field exists
            setRequiresApproval(Boolean(post.needsReview)); // Set toggle state based on needsReview
            setSelectedReviewerId(post.reviewerId || null); // Set the reviewer ID for current selection if needed
            // Set past review details if available (for displaying in ApprovalCard)
            // Assuming post.reviewComment holds the comments.
            // The post.status will be used as actualPostStatus in the ApprovalCard props.
            setFinalReviewComments(post.reviewComment || null);

            // If there was a reviewer and the status indicates a completed review process, try to find their name.
            const reviewedStatuses = ['ready_to_publish', 'scheduled', 'published', 'rejected'];
            if (post.reviewerId && reviewedStatuses.includes(post.status)) {
              // We need to find the reviewer's name from the brand members.
              // This requires brands data to be available. We might need to adjust timing or re-fetch/re-evaluate this if brands load later.
              // For now, let's assume brands might be available or will become available.
              const brandForPost = brands.find((b: any) => b.id === (post.brandId || post.brand_id));
              if (brandForPost && brandForPost.members) {
                const reviewerDetail = brandForPost.members.find((m: any) => m.id === post.reviewerId);
                if (reviewerDetail) {
                  setFinalReviewerInfo({
                    id: reviewerDetail.id,
                    name: `${reviewerDetail.firstName || ''} ${reviewerDetail.lastName || ''}`.trim(),
                    photoURL: reviewerDetail.photoURL || undefined,
                    firstName: reviewerDetail.firstName || '',
                    lastName: reviewerDetail.lastName || ''
                  });
                } else {
                  setFinalReviewerInfo({ id: 'unknown', name: t('common.unknownReviewer', 'Unknown Reviewer') });
                }
              } else {
                setFinalReviewerInfo({ id: 'unknown', name: t('common.unknownReviewer', 'Unknown Reviewer') }); 
              }
            } else {
              setFinalReviewerInfo(null); // No past reviewer to display
            }
          }
          
        } catch (err) {
          logger.error('CreateEditPostPage', 'Error fetching post data', err);
          
          // Enhanced error reporting
          if (err instanceof Error) {
            logger.error('CreateEditPostPage', `Error details: ${err.name} - ${err.message}`, err.stack);
            
            // Check if it's a 404 error
            const is404Error = err.message && err.message.includes('404');
            
            if (is404Error) {
              // Special handling for 404 (post not found)
              setError(`Post with ID "${postId}" could not be found. It may have been deleted.`);
              
              // Show a button to go back to posts list after delay
              setTimeout(() => {
                setError(prev => prev + "\n\nYou will be redirected to the posts list in 5 seconds...");
                
                // After 5 seconds, redirect to the posts list
                setTimeout(() => {
                  navigate('/my-content-hub');
                }, 5000);
              }, 2000);
            } else {
              // Other errors
              setError(`Failed to load post: ${err.message}`);
            }
          } else {
            setError('Failed to load post: Unknown error');
          }
        } finally {
          setIsLoading(false);
          // Loading complete - removed excessive logging
        }
      };
      
      fetchPost();
    }
  }, [isEditMode, postId, navigate]);

  // Effect to update reviewers list when selected brand changes
  useEffect(() => {
    if (selectedBrand && brands.length > 0) {
      setIsLoadingReviewers(true);
      const brandObj = brands.find((b: any) => b.id === selectedBrand);
      
      // Fetch all potential reviewers (users with canPublish)
      const potentialReviewers = (brandObj?.members || [])
        .filter((member: any) => 
            member.status === 'active' && 
            member.permissions?.actions?.post?.canPublish === true
        )
        .map((member: any) => ({ // Map to Reviewer structure
            id: member.id,
            firstName: member.firstName || 'Unnamed',
            lastName: member.lastName || 'User',
            photoURL: member.photoURL || undefined,
        }));

      logger.info('CreateEditPostPage', `Found ${potentialReviewers.length} potential reviewers for brand ${selectedBrand}`);
      setReviewers(potentialReviewers);
      setIsLoadingReviewers(false);
    } else if (!selectedBrand) {
      // Clear reviewers if no brand is selected
      setReviewers([]);
      setSelectedReviewerId(null);
    }
  }, [selectedBrand, brands]);

  // Auto-select default brand when brands are loaded
  useEffect(() => {
    // Only auto-select if no brand is currently selected and brands are available
    if (!selectedBrand && brands.length > 0 && !isEditMode) {
      if (brands.length === 1) {
        // If only one brand, select it automatically
        setSelectedBrand(brands[0].id);
      } else {
        // If multiple brands, select the one with the most connected social accounts
        const brandWithMostAccounts = brands.reduce((bestBrand: any, currentBrand: any) => {
          const currentAccountCount = (currentBrand.social_accounts || []).length;
          const bestAccountCount = (bestBrand.social_accounts || []).length;
          return currentAccountCount > bestAccountCount ? currentBrand : bestBrand;
        }, brands[0]);
        
        setSelectedBrand(brandWithMostAccounts.id);
      }
    }
  }, [brands, selectedBrand, isEditMode]);

  // Separate effect to handle reviewer selection when approval is toggled or reviewers change
  useEffect(() => {
    if (requiresApproval && reviewersLength > 0) {
      const currentReviewerIsValid = reviewers.some((r: Reviewer) => r.id === selectedReviewerId);
      if (!currentReviewerIsValid) {
        const newReviewerId = reviewers[0]?.id || null;
        setSelectedReviewerId(newReviewerId);
      }
    } else if (!requiresApproval) {
      setSelectedReviewerId(null);
    }
  }, [requiresApproval, reviewersLength]); // Remove problematic dependencies to prevent infinite loop

  // Handle brand selection
  const handleBrandSelect = (brandId: string) => {
    setSelectedBrand(brandId);
  };

  // Get TikTok username and profile image for the selected brand
  const getTikTokAccountInfo = () => {
    const selectedBrandObj = brands.find((b: any) => b.id === selectedBrand);
    if (!selectedBrandObj || !selectedBrandObj.social_accounts) return { username: '', picture_url: '' };
    
    const tiktokAccount = selectedBrandObj.social_accounts.find(
      (account: any) => account.platform.toLowerCase() === 'tiktok'
    );
    
    if (!tiktokAccount) return { username: '', picture_url: '' };
    
    return {
      username: tiktokAccount.username || '',
      picture_url: tiktokAccount.profile_url || tiktokAccount.picture_url || ''
    };
  };

  // Get a specific message about what's preventing publishing
  const getPublishButtonTooltip = () => {
    if (!selectedBrand) {
      return 'Select a brand to publish';
    }
    
    if (!content.trim()) {
      return 'Add content to your post';
    }
    
    if (selectedPlatforms.length === 0) {
      return 'Select at least one platform';
    }
    
    if (selectedPlatforms.includes('tiktok')) {
      if (!tiktokSettings.userAgreedToTerms) {
        return "Agree to TikTok's terms to publish";
      }

      if (!tiktokSettings.privacy) {
        return "Select a privacy level for TikTok";
      }

      // Check specifically for disclosure toggle on but no options selected
      if (tiktokSettings.discloseContent && tiktokSettings.disclosureType.length === 0) {
        return t('tooltips.publishDisabled.tiktokDisclosure', 'You need to indicate if your content promotes yourself, a third party, or both.');
      }

      if (tiktokSettings.discloseContent && !tiktokSettings.isValid) {
        return "Complete all required TikTok brand disclosure fields";
      }
    }

    // Add checks for approval
    if (requiresApproval) {
        if (reviewers.length === 0) {
            return "Cannot request approval: No reviewers found for this brand.";
        }
        if (!selectedReviewerId) {
            return "Select a reviewer to send for approval";
        }
    }
    
    return 'Publish post now';
  };

  // Check if the disclosure issue is the specific blocker
  const isDisclosureIssueBlocking = () => {
    return selectedPlatforms.includes('tiktok') && 
           tiktokSettings.discloseContent && 
           tiktokSettings.disclosureType.length === 0;
  };

  // Check if we can publish
  const canPublish = () => {
    if (!selectedBrand || !content.trim() || selectedPlatforms.length === 0) {
      return false;
    }

    if (selectedPlatforms.includes('tiktok')) {
      if (!tiktokSettings.userAgreedToTerms || !tiktokSettings.privacy) {
        return false;
      }

      // Also prevent publishing when disclosure is on but no options selected
      if (tiktokSettings.discloseContent && tiktokSettings.disclosureType.length === 0) {
        return false;
      }

      if (tiktokSettings.discloseContent && !tiktokSettings.isValid) {
        return false;
      }
    }

    // Check approval requirements if toggled on
    if (requiresApproval) {
        if (!selectedReviewerId) {
            return false; // Cannot publish if approval required but no reviewer selected
        }
        if (reviewers.length === 0) {
             return false; // Cannot publish if approval required but no reviewers available
        }
    }

    return true;
  };

  // Get all connected social accounts for the selected brand
  const getConnectedSocialAccounts = () => {
    const selectedBrandObj = brands.find((b: any) => b.id === selectedBrand);
    if (!selectedBrandObj || !selectedBrandObj.social_accounts) return [];
    
    return selectedBrandObj.social_accounts
      .filter((account: any) => account.platform && account.username)
      .map((account: any) => ({
        platform: account.platform.toLowerCase() as SocialPlatform,
        username: account.username,
        picture_url: account.profile_url || account.picture_url || ''
      }));
  };

  const tiktokAccountInfo = getTikTokAccountInfo();
  const connectedAccounts = getConnectedSocialAccounts();

  // Handle brand safety disclosure type changes
  const handleDisclosureTypeChange = (type: string, checked: boolean) => {
    const updatedTypes = checked 
      ? [...tiktokSettings.disclosureType, type] 
      : tiktokSettings.disclosureType.filter(t => t !== type);
    
    setTiktokSettings({
      ...tiktokSettings,
      disclosureType: updatedTypes
    });
  };

  // Function to get missing platform connections
  const getMissingPlatformConnections = () => {
    if (!selectedBrand || selectedPlatforms.length === 0) return [];
    
    const brandObj = brands.find((b: any) => b.id === selectedBrand);
    const connectedPlatformsForCheck = (brandObj?.social_accounts || []).map((acc: any) => acc.platform.toLowerCase());
    
    return selectedPlatforms.filter(
      platform => !connectedPlatformsForCheck.includes(platform.toLowerCase())
    );
  };

  const missingPlatforms = getMissingPlatformConnections();

  // Work with Preview Card and PlatformsCard
  const getBrandById = (brandId: string) => {
    return brands.find((b: any) => b.id === brandId) || null;
  };

  const selectedBrandObj = getBrandById(selectedBrand);

  const setPublishConfirmWithLogging = async (value: { show: boolean, postId?: string }) => {
    // If we're showing the modal and have a post ID, fetch the complete post data first
    if (value.show && value.postId) {
      try {
        // Fetch the post to get the most up-to-date data including media URL
        const postData = await getPost(value.postId);
        
        // Update the media URL if it exists in the response
        if (postData && postData.media && postData.media.url) {
          setMediaUrl(postData.media.url);
        }
        
        // Now set the state to show the modal with updated data
        setShowPublishConfirm(value);
      } catch (error) {
        logger.error('CreateEditPostPage', 'Error fetching post data for confirmation', error);
        // Still show the modal even if fetching fails
        setShowPublishConfirm(value);
      }
    } else {
      // Just update the state directly for hiding the modal
      setShowPublishConfirm(value);
    }
  };

  const handleSubmit = async (shouldPublish = false) => {
    try {
      setIsSaving(true);
      // Set draft saving state when saving as draft
      if (!shouldPublish) {
        setIsSavingDraft(true);
      }
      setError(null);
      
      // --- Check for missing accounts BEFORE validation --- 
      if (shouldPublish && selectedBrand && selectedPlatforms.length > 0) {
        const brandObj = brands.find((b: any) => b.id === selectedBrand);
        const connectedPlatformsForSubmit = (brandObj?.social_accounts || []).map((acc: any) => acc.platform.toLowerCase());
        const missingAccountPlatforms = selectedPlatforms.filter(
          platform => !connectedPlatformsForSubmit.includes(platform.toLowerCase())
        );

        if (missingAccountPlatforms.length > 0) {
          logger.warn('CreateEditPostPage', `Missing account connections for platforms: ${missingAccountPlatforms.join(', ')}`);
          setShowSaveDraftModal({
            show: true,
            missingPlatforms: missingAccountPlatforms,
          });
          setIsSaving(false); // Stop loading indicator
          return; // Exit handleSubmit early
        }
      }
      // --- End Check --- 

      // Validate required fields
      if (!selectedBrand) {
        throw new Error('Please select a brand');
      }

      if (!content.trim()) {
        throw new Error('Content cannot be empty');
      }
      
      // Enhanced TikTok validation
      if (shouldPublish && selectedPlatforms.includes('tiktok')) {
        const tiktokErrors = [];
        if (!tiktokSettings.userAgreedToTerms) {
          tiktokErrors.push("You must agree to TikTok's Music Usage Confirmation");
        }
        if (!tiktokSettings.privacy) {
          tiktokErrors.push("You must select a privacy level for your TikTok post");
        }
        if (tiktokErrors.length > 0) {
          throw new Error(tiktokErrors.join('\n'));
        }
      }

      // Prepare the base payload - match exact structure of working curl
      // Ensure format is always valid before sending to API
      const validatedFormat = (format === 'reel') ? 'reel' : 'regular';
      
      // Build platformSettings exactly like the working curl
      const platformSettings: any = {};
      
      if (selectedPlatforms.includes('tiktok')) {
        platformSettings.tiktok = {
          privacy_level: tiktokSettings.privacy,
          disable_duet: !tiktokSettings.allowDuets,
          disable_comment: !tiktokSettings.allowComments,
          disable_stitch: !tiktokSettings.allowStitch,
          brand_safety_brand_owned: tiktokSettings.disclosureType.includes('brand'),
          brand_safety_paid_partnership: tiktokSettings.disclosureType.includes('branded')
        };
      }
      
      if (selectedPlatforms.includes('instagram')) {
        platformSettings.instagram = {
          video_cover_timestamp_ms: 1000,
          share_to_feed: true
        };
      }
      
      if (selectedPlatforms.includes('youtube')) {
        platformSettings.youtube = {
          title: youtubeSettings.title || content.substring(0, 100),
          privacy: youtubeSettings.privacy,
          tags: youtubeSettings.tags,
          category_id: "22"
        };
      }
      
      if (selectedPlatforms.includes('facebook')) {
        platformSettings.facebook = {
          title: content.substring(0, 100)
        };
      }
      
      if (selectedPlatforms.includes('linkedin')) {
        platformSettings.linkedin = {
          organizationId: linkedinSettings.organizationId,
          visibility: linkedinSettings.visibility
        };
      }

      const basePayload: any = {
        brandId: selectedBrand,
        title: "", // Required field, empty string default
        content,
        format: validatedFormat,
        platforms: selectedPlatforms,
        // Explicitly add fields that are in the working curl
        needsReview: requiresApproval && selectedReviewerId ? true : false,
        reviewerId: requiresApproval && selectedReviewerId ? selectedReviewerId : "",
        tags: [], // Required field, empty array default
        platformSettings: platformSettings
      };

      // Enhanced media handling for existing media URLs
      if (mediaUrl && !media) {
        basePayload.media = [{
          url: mediaUrl,
          type: (mediaType || 'image').toUpperCase(),
          thumbnailUrl: mediaUrl,
          description: ""
        }];
      }

      // Updated status and scheduling logic
      if (activeScheduledAtUtc) { // If an active schedule is set via ScheduleCard
        basePayload.status = 'scheduled';
        basePayload.scheduledAt = activeScheduledAtUtc;
      } else if (shouldPublish && requiresApproval && selectedReviewerId) {
        basePayload.status = 'waiting_for_approval';
        // needsReview and reviewerId already set above
      } else {
        basePayload.status = shouldPublish ? 'published' : 'draft';
        basePayload.scheduledAt = null; // Explicitly nullify if not actively scheduling
      }

      // **NEW TWO-STEP UPLOAD PROCESS FOR EDIT MODE**
      if (isEditMode && media instanceof File) {
        
        // Step 1: Upload file to storage service first
        try {
          const user = auth.currentUser;
          if (!user) {
            throw new Error('No authenticated user for file upload');
          }

          const token = await user.getIdToken();
          const uploadFormData = new FormData();
          uploadFormData.append('file', media);
          if (selectedBrand) {
            uploadFormData.append('brandId', selectedBrand);
          }

          const uploadResponse = await fetch(`${API_BASE_URL}/storage/upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
              // Don't set Content-Type header for FormData
            },
            body: uploadFormData
          });

          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            logger.error('CreateEditPostPage', 'File upload failed', errorText);
            throw new Error(`File upload failed: ${uploadResponse.status} ${errorText}`);
          }

          const uploadResult = await uploadResponse.json();

          // Extract the uploaded file URL - handle actual API response format
          const uploadedMediaUrl = uploadResult.publicUrl || uploadResult.url || uploadResult.fileUrl || uploadResult.data?.url;
          if (!uploadedMediaUrl) {
            logger.error('CreateEditPostPage', 'No URL in upload response', uploadResult);
            throw new Error('File upload response missing URL');
          }

          // Step 2: Update post with the uploaded media URL
          basePayload.media = [{
            url: uploadedMediaUrl,
            type: (mediaType || (media.type.startsWith('video/') ? 'video' : 'image')).toUpperCase(),
            thumbnailUrl: uploadedMediaUrl, // For both images and videos
            description: "" // Optional description field
          }];

          const updatedPost = await updatePostMutation.mutateAsync({ 
            postId: postId!, 
            postData: basePayload, 
            isFormData: false 
          });

          if (shouldPublish && !activeScheduledAtUtc) {
            await setPublishConfirmWithLogging({ show: true, postId: postId! });
          }

        } catch (uploadError) {
          logger.error('CreateEditPostPage', 'Two-step upload process failed', uploadError);
          throw new Error(`Failed to upload and update post: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
        }
      }
      // **EXISTING LOGIC FOR NON-EDIT OR NO-NEW-FILE SCENARIOS**
      else {
        // Handle CREATE mode with new file - upload first, then JSON create with media URL
        if (!isEditMode && media instanceof File && media.size > 0) {
          try {
            const user = auth.currentUser;
            if (!user) {
              throw new Error('No authenticated user for file upload');
            }

            const token = await user.getIdToken();
            const uploadFormData = new FormData();
            uploadFormData.append('file', media);
            if (selectedBrand) {
              uploadFormData.append('brandId', selectedBrand);
            }

            const uploadResponse = await fetch(`${API_BASE_URL}/storage/upload`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: uploadFormData
            });

            if (!uploadResponse.ok) {
              const errorText = await uploadResponse.text();
              logger.error('CreateEditPostPage', 'File upload failed (create)', errorText);
              throw new Error(`File upload failed: ${uploadResponse.status} ${errorText}`);
            }

            const uploadResult = await uploadResponse.json();
            const uploadedMediaUrl = uploadResult.publicUrl || uploadResult.url || uploadResult.fileUrl || uploadResult.data?.url;
            if (!uploadedMediaUrl) {
              logger.error('CreateEditPostPage', 'Upload response missing URL (create)', uploadResult);
              throw new Error('File upload response missing URL');
            }

            basePayload.media = [{
              url: uploadedMediaUrl,
              type: (media.type.startsWith('video/') ? 'VIDEO' : 'IMAGE'),
              thumbnailUrl: uploadedMediaUrl,
              description: ""
            }];

            const newPost = await createPostMutation.mutateAsync({
              postData: basePayload,
              isFormData: false
            });

            const newPostId = handleApiResponse(newPost);
            if (shouldPublish && !activeScheduledAtUtc && newPostId) {
              await setPublishConfirmWithLogging({ show: true, postId: newPostId });
            }

          } catch (createErr) {
            logger.error('CreateEditPostPage', 'Failed to create post with uploaded media', createErr);
            throw createErr;
          }
        } else {
          // Standard JSON submission (no file, or edit mode without new file)
          
          if (isEditMode && postId) {
            const updatedPost = await updatePostMutation.mutateAsync({ 
              postId: postId, 
              postData: basePayload, 
              isFormData: false 
            });
            if (shouldPublish && !activeScheduledAtUtc) {
              await setPublishConfirmWithLogging({ show: true, postId });
            }
          } else if (!isEditMode) {
            const newPost = await createPostMutation.mutateAsync({ 
              postData: basePayload, 
              isFormData: false 
            });
            const newPostId = handleApiResponse(newPost);
            if (shouldPublish && !activeScheduledAtUtc && newPostId) {
              await setPublishConfirmWithLogging({ show: true, postId: newPostId });
            } else if (shouldPublish && !activeScheduledAtUtc && !newPostId) {
              // If we couldn't get an ID but post was created, just navigate
              showToast('Post created successfully but unable to show publish confirmation', 'success');
              navigate('/my-content-hub');
              return; // Exit early to avoid the navigation logic below
            }
          }
        }
      }

      // Navigate or show toast based on action
      if (activeScheduledAtUtc) { // If scheduled
         showToast(t('toasts.scheduleSuccess', 'Post scheduled successfully!'), 'success');
         navigate('/my-content-hub');
      } else if (!shouldPublish && !requiresApproval) { // Saved as draft (not for approval)
        navigate('/my-content-hub');
      } else if (shouldPublish && requiresApproval && selectedReviewerId) { // Sent for approval
        showToast(t('toasts.approvalRequested', 'Post sent for approval successfully!'), 'success');
        navigate('/my-content-hub');
      }
      // If shouldPublish is true but not scheduled and not for approval, publish modal handles navigation.

    } catch (err: any) {
      logger.error('Submission error:', err);
      
      // Enhanced error handling
      let errorMessage = err.message || 'Failed to save post';
      
      // Check for HTML error responses
      if (typeof err.message === 'string' && err.message.includes('<!DOCTYPE html>')) {
        errorMessage = 'Server error occurred. Please try again later.';
      }
      
      // Check for network errors
      if (err.name === 'NetworkError' || err.message.includes('Network')) {
        errorMessage = 'Network error occurred. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setIsSaving(false);
      setIsSavingDraft(false);
    }
  };

  // Function to handle saving draft from the new modal
  const handleSaveDraftFromModal = async () => {
    // Set draft saving state
    setIsSavingDraft(true);
    try {
      // Re-use handleSubmit logic, but force it to save as draft
      await handleSubmit(false);
      // Show toast message
      showToast(t('toasts.draftSavedDueToMissingAccounts', 'Post saved as draft due to missing account connections.'), 'info');
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Add format change handler
  const handleFormatChange = (newFormat: 'regular' | 'reel') => {
    setFormat(newFormat);
  };

  // Add PostContentCard specific props
  const handlePostContentFormatChange = (newFormat: 'regular' | 'reel') => {
    setFormat(newFormat);
  };

  // Updated handleScheduleChange to set activeScheduledAtUtc
  const handleScheduleChange = (newUtcDateString: string | null, timezone?: string) => {
    setActiveScheduledAtUtc(newUtcDateString);
    // Optionally, if the parent needs to know the timezone chosen in ScheduleCard, handle `timezone` here.
  };

  // Handle platform toggles for PlatformsCard
  const handlePlatformToggle = (platform: SocialPlatform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  // Add publisher confirmation handlers
  const handlePublishConfirm = () => {
    try {
      navigate('/my-content-hub');
    } catch (error) {
      logger.error('CreateEditPostPage', 'Error navigating after publish', error);
    }
  };

  // Prepare YouTube settings with correct types
  const youtubeSettingsWithCorrectTypes = {
    ...youtubeSettings,
    privacy: youtubeSettings.privacy as 'public' | 'private' | 'unlisted'
  };

  // Handler for submitting the review comment modal
  const handleReviewSubmit = async (comment: string) => {
    if (!postId || !reviewDecision) {
      logger.error('CreateEditPostPage', 'Missing postId or review decision for submission');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await submitPostReview(postId, reviewDecision, comment);
      showToast(
        t('toasts.reviewSubmitSuccess', 'Review submitted successfully!'),
        'success'
      );
      // Navigate back to the content hub or appropriate list after successful submission
      navigate('/my-content-hub'); // Or potentially use -1 if appropriate
    } catch (error) {
      logger.error('CreateEditPostPage', `Error submitting ${reviewDecision} review for post ${postId}`, error);
      showToast(
        error instanceof Error ? error.message : t('toasts.reviewSubmitError', 'Failed to submit review'),
        'error'
      );
    } finally {
      setIsReviewModalOpen(false); // Close modal regardless of outcome
      setIsSaving(false);
      setReviewDecision(null); // Reset decision
    }
  };

  // AI Review functions
  const handleCopyContent = () => {
    if (aiResponse) {
      navigator.clipboard.writeText(aiResponse.enhancedContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleApplyAiContent = () => {
    if (aiResponse) {
      setContent(aiResponse.enhancedContent);
      setAiResponse(null);
    }
  };

  // Determine if the current user is the reviewer for this post
  const currentUser = auth.currentUser;
  // Ensure initialData and status are checked safely
  const isReviewer = isEditMode && 
                     initialData && // Check if initialData exists
                     (initialData as any).status === 'waiting_for_approval' && // Cast to any for status check
                     currentUser?.uid === (initialData as any).reviewerId; // Cast to any for reviewerId access

  // Determine button text dynamically
  let publishButtonText = t('actions.publishNow', 'Publish Now');
  if (requiresApproval && selectedReviewerId) {
    publishButtonText = t('actions.sendForApproval', 'Send for Approval');
  } else if (activeScheduledAtUtc) { // If a schedule is active
    publishButtonText = t('actions.schedule', 'Schedule'); 
  }

  if (isLoading) {
    return (
      // Skeleton Loader for the whole page
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0 animate-pulse">
        {/* Skeleton Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="w-[95%] mx-auto px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div> {/* Back button */} 
                <div>
                  <div className="h-6 w-48 bg-gray-200 rounded mb-1"></div> {/* Title */} 
                  <div className="h-4 w-64 bg-gray-200 rounded"></div> {/* Subtitle */} 
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="h-10 w-28 bg-gray-200 rounded-lg"></div> {/* Save button */} 
                <div className="h-10 w-32 bg-gray-200 rounded-lg"></div> {/* Publish button */} 
              </div>
            </div>
          </div>
        </div>

        {/* Skeleton Main Content */}
        <div className="w-[95%] mx-auto py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
            {/* Skeleton Left Column */}
            <div className="space-y-4 lg:space-y-6 col-span-1 xl:col-span-2 min-w-0">
              {/* Skeleton Brand/Schedule Grid */} 
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <div className="h-48 bg-gray-200 rounded-xl"></div> {/* Brand/Platform Card */} 
                <div className="h-48 bg-gray-200 rounded-xl"></div> {/* Schedule Card */} 
              </div>
              {/* Skeleton AI Card */}
              <div className="h-64 bg-gray-200 rounded-xl"></div>
              {/* Skeleton Approval Card */}
              <div className="h-40 bg-gray-200 rounded-xl"></div>
              {/* Skeleton Content Card */} 
              <div className="h-60 bg-gray-200 rounded-xl"></div>
            </div>

            {/* Skeleton Right Column */}
            <div className="xl:col-span-1">
              <div className="sticky top-28 space-y-6">
                 {/* Skeleton Preview Card */}
                <div className="h-96 bg-gray-200 rounded-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="w-[95%] mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:text-primary-600 transition-colors"
                aria-label={t('common.back', 'Back')}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 font-title">
                  {isEditMode 
                    ? t('pages.editPost.title', 'Edit Post') 
                    : t('pages.createPost.title', 'Create Post')}
                </h1>
                <p className="text-sm text-gray-500">
                  {isEditMode 
                    ? t('pages.editPost.subtitle', 'Update your social media post') 
                    : t('pages.createPost.subtitle', 'Create a new social media post')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Conditional Buttons for Reviewer */}
              {isReviewer ? (
                <>
                  <button
                    onClick={() => handleSubmit(false)} // Allow reviewer to save draft updates?
                    className="flex-1 sm:flex-initial inline-flex items-center px-4 py-2 text-primary-600 bg-white border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                    disabled={isSaving}
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {t('common.saveDraft', 'Save Draft')}
                  </button>
                  <button
                    onClick={() => { setReviewDecision('rejected'); setIsReviewModalOpen(true); }}
                    className="flex-1 sm:flex-initial inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    disabled={isSaving}
                  >
                    {t('actions.reject', 'Reject')}
                  </button>
                  <button
                    onClick={() => { setReviewDecision('approved'); setIsReviewModalOpen(true); }}
                    className="flex-1 sm:flex-initial inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    disabled={isSaving}
                  >
                    {t('actions.approve', 'Approve')}
                  </button>
                </>
              ) : (
                <> {/* Original Buttons */}
                  <button
                    onClick={() => handleSubmit(false)}
                    className="flex-1 sm:flex-initial inline-flex items-center px-4 py-2 text-primary-600 bg-white border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                    disabled={isSaving}
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {t('common.saveDraft', 'Save Draft')}
                  </button>
                  <div className="relative">
                    <button
                      ref={publishBtnRef}
                      onClick={() => handleSubmit(true)}
                      onMouseOver={() => setShowTooltip(true)}
                      onMouseOut={() => setShowTooltip(false)}
                      onFocus={() => setShowTooltip(true)}
                      onBlur={() => setShowTooltip(false)}
                      className={`flex-1 sm:flex-initial inline-flex items-center px-4 py-2 ${
                        canPublish()
                          ? 'bg-primary-600 hover:bg-primary-700'
                          : 'bg-gray-300 cursor-not-allowed opacity-50'
                      } text-white rounded-lg transition-colors`}
                      disabled={isSaving || !canPublish()}
                    >
                      <Send className={`w-5 h-5 mr-2 ${!canPublish() ? 'opacity-50' : ''}`} />
                      {publishButtonText}
                    </button>
                    
                    {showTooltip && !canPublish() && isDisclosureIssueBlocking() && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50 w-64">
                         <Alert 
                           type="warning"
                           size="xs"
                           message="tooltips.publishDisabled.tiktokDisclosure"
                         />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Missing Platform Connections Warning */}
          {missingPlatforms.length > 0 && selectedBrandObj && (
            <div className="mt-4 flex justify-end">
              <div className="text-red-600 text-sm font-medium text-right">
                <div>
                  {t('warnings.noAccountConnectedForBrandMultiple', '{{brandName}} does not have connected accounts for: {{platformList}}.', {
                    brandName: selectedBrandObj.name,
                    platformList: missingPlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')
                  })}
                </div>
                <div className="mt-1">
                  {t('warnings.saveAsDraftOnlyNote', 'You can save as draft but not publish yet.')}
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation in Header */}
          <div className="flex justify-end border-b border-gray-200 mt-4 -mb-4">
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'preview'
                  ? 'text-primary-600 border-primary-600'
                  : 'text-black border-transparent hover:text-primary-600'
              }`}
            >
              <Eye className="w-4 h-4" />
              {t('tabs.preview', 'Preview')}
            </button>
            <button
              onClick={() => setActiveTab('ai-time-suggestions')}
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'ai-time-suggestions'
                  ? 'text-primary-600 border-primary-600'
                  : 'text-black border-transparent hover:text-primary-600'
              }`}
            >
              <Clock className="w-4 h-4" />
              {t('tabs.aiTimeSuggestions', 'AI Time Suggestions')}
            </button>
            <button
              onClick={() => setActiveTab('planning')}
              className={`px-6 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'planning'
                  ? 'text-primary-600 border-primary-600'
                  : 'text-black border-transparent hover:text-primary-600'
              }`}
            >
              <Calendar className="w-4 h-4" />
              {t('tabs.planning', 'Planning')}
            </button>
          </div>

          {error && (
             <div className="mt-4">
               <Alert 
                 type="error"
                 message={error}
                 className="text-sm"
               />
             </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="w-[95%] mx-auto py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
          {/* Left Column */}
          <div className="space-y-4 lg:space-y-6 col-span-1 xl:col-span-2 min-w-0">
            {/* Updated Left Column Structure - Post Content first */}
            <div className="space-y-4 lg:space-y-6 col-span-1 xl:col-span-2 min-w-0">
              {/* Post Content - Moved to the top */}
              <div className="min-w-0">
                <PostContentCard
                  content={content}
                  onContentChange={setContent}
                  media={media}
                  onMediaChange={setMedia}
                  existingMediaUrl={mediaUrl}
                  existingMediaType={mediaType}
                  onClearMedia={handleClearMedia}
                  status={currentPostStatus}
                  format={format}
                  onFormatChange={setFormat}
                  brandId={selectedBrand}
                  selectedPlatforms={selectedPlatforms}
                  aiResponse={aiResponse}
                  onAiResponseChange={setAiResponse}
                />
              </div>

              {/* AI Review Card - Only show if AI analysis exists */}
              {aiResponse && (
                <div className="min-w-0">
                  <PostAIReviewCard
                    aiResponse={aiResponse}
                    copied={copied}
                    onCopyContent={handleCopyContent}
                    onApplyAiContent={handleApplyAiContent}
                  />
                </div>
              )}

              {/* Grid container for Brand/Platforms and Media cards */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6">
                {/* Brands and Platforms Card */}
                <div className="min-w-0">
                  <BrandsAndPlatformsCard
                    selectedBrandId={selectedBrand}
                    onBrandSelect={handleBrandSelect}
                    selectedPlatforms={selectedPlatforms}
                    onPlatformToggle={handlePlatformToggle}
                    onFormatChange={setFormat}
                    format={format}
                    connectedAccounts={connectedAccounts}
                    tiktokAccount={tiktokAccountInfo}
                  />
                </div>

                {/* Media Post Card */}
                <div className="min-w-0">
                  <MediaPostCard
                    media={media}
                    onMediaChange={setMedia}
                    onClearMedia={() => setMedia(null)}
                    existingMediaUrl={mediaUrl}
                    existingMediaType={mediaType}
                    format={format}
                    onFormatChange={setFormat}
                    brandId={selectedBrand}
                    content={content}
                    selectedPlatforms={selectedPlatforms}
                    onExistingMediaChange={(url) => {
                      if (url) {
                        setMediaUrl(url);
                        setMediaType('image'); // Generated images are always images
                      } else {
                        setMediaUrl(null);
                        setMediaType(null);
                      }
                    }}
                  />
                </div>
              </div>

              {/* Platform-specific Settings Cards */}
              {selectedPlatforms.includes('tiktok') && (
                <div className="min-w-0">
                  <TikTokComplianceCard
                    tiktokUsername={tiktokAccountInfo.username}
                    tiktokProfileImage={tiktokAccountInfo.picture_url}
                    allowComments={tiktokSettings.allowComments}
                    allowDuets={tiktokSettings.allowDuets}
                    allowStitch={tiktokSettings.allowStitch}
                    discloseContent={tiktokSettings.discloseContent}
                    privacy={tiktokSettings.privacy}
                    disclosureType={tiktokSettings.disclosureType}
                    userAgreedToTerms={tiktokSettings.userAgreedToTerms}
                    onAllowCommentsChange={(allow) => setTiktokSettings({ ...tiktokSettings, allowComments: allow })}
                    onAllowDuetsChange={(allow) => setTiktokSettings({ ...tiktokSettings, allowDuets: allow })}
                    onAllowStitchChange={(allow) => setTiktokSettings({ ...tiktokSettings, allowStitch: allow })}
                    onDiscloseContentChange={(disclose, type) => setTiktokSettings({ 
                      ...tiktokSettings, 
                      discloseContent: disclose,
                      disclosureType: type ? type.split(',') : [] 
                    })}
                    onPrivacyChange={(privacy) => setTiktokSettings({ ...tiktokSettings, privacy })}
                    onUserAgreedToTermsChange={(agreed) => setTiktokSettings({ ...tiktokSettings, userAgreedToTerms: agreed })}
                    onValidationChange={(isValid) => setTiktokSettings({ ...tiktokSettings, isValid })}
                  />
                </div>
              )}

              {selectedPlatforms.includes('youtube') && (
                <div className="min-w-0">
                  <YouTubePostSettingsCard
                    title={youtubeSettings.title}
                    tags={youtubeSettings.tags}
                    privacy={youtubeSettings.privacy as 'public' | 'private' | 'unlisted'}
                    onTitleChange={(title) => setYoutubeSettings({ ...youtubeSettings, title })}
                    onTagsChange={(tags) => setYoutubeSettings({ ...youtubeSettings, tags })}
                    onPrivacyChange={(privacy) => setYoutubeSettings({ ...youtubeSettings, privacy })}
                  />
                </div>
              )}

              {selectedPlatforms.includes('linkedin') && (
                <div className="min-w-0">
                  <LinkedInPostSettingsCard
                    organizationId={linkedinSettings.organizationId}
                    visibility={linkedinSettings.visibility}
                    selectedBrandId={selectedBrand}
                    onOrganizationIdChange={(orgId) => setLinkedinSettings({ ...linkedinSettings, organizationId: orgId })}
                    onVisibilityChange={(visibility) => setLinkedinSettings({ ...linkedinSettings, visibility })}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Tab Content Only */}
          <div className="xl:col-span-1">
            {/* Sticky container for tab content */}
            <div className="sticky top-28">
              {/* Tab Content - Direct content without card wrapper */}
              <div className="space-y-6">
                {activeTab === 'preview' && (
                  <PreviewCard
                    content={content}
                    selectedPlatforms={selectedPlatforms}
                    media={media}
                    existingMediaUrl={mediaUrl}
                    existingMediaType={mediaType}
                    format={format}
                    selectedBrand={selectedBrand}
                    brandName={selectedBrandObj?.name}
                    brandLogo={selectedBrandObj?.logo}
                    brands={brands}
                  />
                )}

                {activeTab === 'ai-time-suggestions' && (
                  <AIBestTimeCard
                    brandId={selectedBrand}
                    selectedPlatforms={selectedPlatforms}
                    brandName={selectedBrandObj?.name}
                  />
                )}

                {activeTab === 'planning' && (
                  <div className="space-y-6">
                    <ApprovalCard
                      requiresApproval={requiresApproval}
                      selectedReviewerId={selectedReviewerId}
                      reviewers={reviewers}
                      onApprovalToggle={setRequiresApproval}
                      onReviewerChange={setSelectedReviewerId}
                      isLoadingReviewers={isLoadingReviewers}
                      currentUserId={currentUserId}
                      actualPostStatus={currentPostStatus}
                      finalReviewer={finalReviewerInfo}
                      finalReviewComments={finalReviewComments}
                    />
                    
                    <ScheduleCard
                      initialScheduledAtUtc={initialScheduledAtUtcFromDb}
                      onScheduleChange={setActiveScheduledAtUtc}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Waiting Modal */}
      <WaitingModal
        isOpen={isSaving}
        title="Saving your post"
        message="Please wait while we save your post..."
        gifUrl="https://media.socialness.ai/loading/unicorn.gif"
      />

      {/* Publish Confirmation */}
      {(() => {
        // Log the brand object being passed to the modal right before rendering
        return (
          <PublishConfirmationModal
            isOpen={showPublishConfirm.show}
            postId={showPublishConfirm.postId || ''}
            onClose={() => {
              void setPublishConfirmWithLogging({ show: false });
            }}
            onConfirm={handlePublishConfirm}
            content={content}
            platforms={selectedPlatforms}
            brand={selectedBrandObj as any}
            hasMedia={Boolean(media || mediaUrl)}
            mediaType={mediaType || undefined}
            format={format}
            scheduledAt={activeScheduledAtUtc}
            media={media}
            mediaUrl={mediaUrl}
            connectedAccounts={connectedAccounts}
          />
        );
      })()}
      
      {/* Save Draft Instead Modal */}
      <SaveDraftInsteadModal
        isOpen={showSaveDraftModal.show}
        onClose={() => setShowSaveDraftModal({ show: false, missingPlatforms: [] })}
        onSaveDraft={handleSaveDraftFromModal}
        missingPlatforms={showSaveDraftModal.missingPlatforms}
        brandName={selectedBrandObj?.name}
      />
      
      {/* Review Comment Modal */}
      <ReviewCommentModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        onSubmit={handleReviewSubmit}
        decision={reviewDecision}
      />
    </div>
  );
}; 